import Data from '@/data'
import { loadTree } from '@/data/load'
import {
  atlasNodeLimit,
  historyNextPosition,
  historyPrevPosition,
  passiveNodeLimit,
  TreeEditingState,
  treeNodeCount,
  TreeProperties,
  treeToggleRecording,
  treeViewState,
  validateTreeEmbed
} from '@/logic/tree'
import { NodeData, TreeDataProvider } from '@/logic/treeData'
import Store, { EmbedStore } from '@/store'
import { TreeHandle, TreeViewer } from '@/tree/TreeViewer'
import { AtlasEmbed, Embed, PassivesEmbed, TreeVariant } from '@/types/embed'
import { defaultPlayerClass, ItemId, PlayerClass } from '@/types/profile'
import Tooltip from '@/ui/tooltip'
import { chainedRef, versionNumberToString } from '@/utils'
import {
  faChevronLeft,
  faChevronRight,
  faCircle,
  faEdit,
  faExpandArrowsAlt,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { pick } from 'lodash'
import React, { Dispatch, ReactNode, SetStateAction } from 'react'
import WithTreeData from '../WithTreeData'
import { CopyEmbedBox } from '../copy'
import { AtlasStrategyBar } from './tree/AtlasStrategy'
import { useNoteEditing } from './tree/NoteEditor'
import { FocusAction, TreeDataContext } from './tree/common'
import { PassiveNotes } from './tree/notes'
import { PassiveSidebarContainer } from './tree/sidebar'
import { HistoryStep } from './tree/steps'
import { PassiveTotals } from './tree/summary'
import { PassiveTreeVariants } from './tree/variants'

import { ItemDefinition, JewelSlotDefinition, PassiveSkillDefinition, TattooDefinition } from '@/data/types'
import { LibraryNode } from '@/library'
import { stateGetItem } from '@/logic/stateReducers'
import { skillTattooSets, tattooTribes } from '@/logic/tattoos'
import Dialog from '@/ui/dialog'
import { notFalsy, notNull } from '@/utils/validation'
import { SearchInput, Select, SelectOption, useConfirm } from '@maxroll-media-group/common-libs'
import ItemLibrary, { LibraryClickHandler } from '../ItemLibrary'
import { CustomItemLibrary, ItemFilter } from '../item/ItemList'
import './PassiveEmbed.scss'

const lightEditingState = (variant: TreeVariant): TreeEditingState => ({
  history: variant.history,
  position: 0,
  masteries: variant.masteries,
  jewels: variant.jewels,
  tattoos: variant.tattoos
})

type NavState = Pick<
  TreeEditingState,
  'ascendancyOpen' | 'position' | 'startPosition' | 'hover' | 'masteryPopup' | 'recording' | 'editHistory'
>

export type JewelEditingProps = {
  filter?: ItemFilter
  id: ItemId
  onSelect: (id: ItemId) => void
  onClose: () => void
}

type TreeEmbedProps = {
  embed: PassivesEmbed | AtlasEmbed
  onChange?: (value: Embed | ((prev: Embed) => Embed)) => void
  preview?: string
  children?: ReactNode
  limited?: boolean
  showNotes?: boolean
  hideBackground?: boolean
  startFromActivePos?: boolean
  compact?: boolean
  resetTransform?: any
  jewelEditor?: React.ComponentType<JewelEditingProps>
  profileId?: string
}

function JewelEditor({ filter, id, onSelect, onClose }: JewelEditingProps) {
  return (
    <Dialog title="Edit Jewel" onClose={onClose} className="poe-JewelDialog" resizable="nesw">
      <CustomItemLibrary filter={filter} selected={id} onSelect={onSelect} showEmpty noStats />
    </Dialog>
  )
}

function makeSocketFilter(slot: JewelSlotDefinition): ItemFilter {
  function baseFilter(item: ItemDefinition) {
    if (!item) return false
    if (slot.charm) {
      return item.item_class === 'AnimalCharm'
    } else {
      if (item.item_class === 'AbyssJewel') return true
      if (item.item_class !== 'Jewel') return false
      const size = slot.size === 'Large' ? 3 : slot.size === 'Medium' ? 2 : slot.size === 'Small' ? 1 : 0
      if (item.tags.includes('expansion_jewel_small')) return size >= 1
      if (item.tags.includes('expansion_jewel_medium')) return size >= 2
      if (item.tags.includes('expansion_jewel_large')) return size >= 3
      return true
    }
  }
  return {
    filter: (item) => baseFilter(Data.items[item.base]),
    categories: () =>
      slot.charm
        ? {
            name: 'Charms',
            filter: baseFilter
          }
        : {
            name: 'All Jewels',
            filter: baseFilter,
            children: [
              {
                name: 'Normal Jewels',
                filter: (item: ItemDefinition) =>
                  item.item_class === 'Jewel' && item.domain !== 'affliction_jewel' && baseFilter(item)
              },
              slot.size && {
                name: 'Cluster Jewels',
                filter: (item: ItemDefinition) =>
                  item.item_class === 'Jewel' && item.domain === 'affliction_jewel' && baseFilter(item)
              },
              {
                name: 'Abyss Jewels',
                filter: (item: ItemDefinition) => item.item_class === 'AbyssJewel' && baseFilter(item)
              }
            ].filter(notFalsy)
          }
  }
}

function tattooCategories(skill: PassiveSkillDefinition): LibraryNode {
  if (skill.is_keystone) {
    return {
      name: 'Keystones',
      type: 'tattoo',
      filter: (item: TattooDefinition) => item && item.allocatedSkill
    }
  }
  const sets = skillTattooSets(skill)
  if (!sets) return { name: 'Tattoos', type: 'tattoo', filter: () => false }
  const tattoos = Object.values(Data.tattoos)
    .map((tattoo) => tattoo.item)
    .filter(notNull)
    .filter((id) => sets.includes(Data.items[id].tattoo!.set))
  const tribes = new Set(tattoos.map((id) => Data.items[id].tattoo!.tribe))

  return {
    name: 'Tattoos',
    type: 'tattoo',
    filter: (item: TattooDefinition) => item?.item && sets.includes(Data.items[item.item].tattoo!.set),
    children: [...tribes].map((tribe) => ({
      name: tattooTribes[tribe],
      type: 'tattoo',
      filter: (item: TattooDefinition) =>
        item?.item && sets.includes(Data.items[item.item].tattoo!.set) && Data.items[item.item].tattoo!.tribe === tribe
    }))
  }
}

function useGrantedNodes() {
  return Store.useSelector((state) => {
    if (!state.planner) return
    const equipment = state.planner.equipment.variants[state.planner.equipment.active]
    if (!equipment) return
    const granted: number[] = []
    for (const itemId of Object.values(equipment.items)) {
      const item = stateGetItem(state, itemId)
      if (!item) continue
      for (const stats of Object.values(item.stats)) {
        if (!stats) continue
        for (const [id, value] of Object.entries(stats)) {
          if (
            id === 'mod_granted_passive_hash' ||
            id === 'mod_granted_passive_hash_2' ||
            id === 'mod_granted_passive_hash_3' ||
            id === 'mod_granted_passive_hash_4'
          ) {
            granted.push(value)
          }
        }
      }
    }
    return granted
  }, [])
}

export const PassiveTreeEmbed = React.forwardRef(function PassiveTreeEmbed(
  {
    embed,
    onChange,
    preview,
    children,
    limited,
    search,
    showNotes,
    compact,
    hideBackground,
    startFromActivePos,
    resetTransform,
    jewelEditor: TheJewelEditor = JewelEditor,
    profileId
  }: TreeEmbedProps & {
    search?: string
  },
  ref: React.ForwardedRef<TreeHandle>
) {
  const { getConfirmation, ConfirmationModal } = useConfirm()
  const readOnly = !onChange
  const treeProperties = React.useMemo<TreeProperties>(() => {
    if (embed.type === 'atlas') {
      return {
        version: embed.version,
        readOnly,
        hideBackground,
        atlas: true,
        league: embed.league,
        limit: atlasNodeLimit
      }
    } else {
      const result: TreeProperties = {
        version: embed.version,
        readOnly,
        hideBackground,
        charClass: embed.charClass,
        ascendancy: embed.ascendancy,
        limit: passiveNodeLimit
      }
      const variant = embed.variants[embed.active] ?? embed.variants[0]
      if (variant?.charClass) {
        result.charClass = variant.charClass
        result.ascendancy = variant.ascendancy
      }
      return result
    }
  }, [embed, readOnly, hideBackground])

  const store = Store.useStore()
  const items = Store.useSelector((state) => state.items)

  const [editNode, setEditNode] = React.useState<{
    id: number
    socket?: JewelSlotDefinition
    skill?: PassiveSkillDefinition
  }>()

  const variant = embed.variants[embed.active] || embed.variants[0] || EmbedStore.passives.variants[0]

  const chunkHistory = !(preview || onChange || compact)

  const [navState, setNavState] = React.useState(() => {
    const state: NavState = {
      ascendancyOpen: embed.type === 'passives' && embed.ascendancy != null,
      position: variant.history.length
    }
    if (startFromActivePos && embed.activePos != null) {
      state.position = historyNextPosition(treeProperties, lightEditingState(variant), embed.activePos + 1, items)
      state.startPosition = embed.activePos
    } else if (chunkHistory) {
      state.startPosition = historyPrevPosition(treeProperties, lightEditingState(variant), state.position, items)
    }
    return state
  })

  const activePosRef = React.useRef(-1)
  React.useEffect(() => {
    const activePos = embed.activePos
    if (
      activePos == null ||
      activePos <= 0 ||
      activePos >= variant.history.length ||
      activePos == activePosRef.current
    ) {
      setNavState((state) => {
        if (state.position < 0 || state.position > variant.history.length) {
          return {
            ...state,
            position: Math.max(0, Math.min(state.position, variant.history.length))
          }
        }
        return state
      })
      return
    }

    activePosRef.current = activePos
    setNavState((state) => ({
      ...state,
      startPosition: activePos,
      position: historyNextPosition(treeProperties, lightEditingState(variant), activePos + 1, store.getState().items),
      recording: false
    }))
  }, [embed.activePos, variant, treeProperties, store])

  const embedRef = React.useRef<{
    profileId: string | undefined
    id: number
    active: number
  }>({
    profileId: undefined,
    id: -1,
    active: -1
  })
  const isInitialLoad = React.useRef(true)
  React.useEffect(() => {
    if (
      embedRef.current.id !== embed.id ||
      embedRef.current.active !== embed.active ||
      embedRef.current.profileId !== profileId
    ) {
      // Force the tree to keep the starting pos at active pos
      if (isInitialLoad.current && startFromActivePos) {
        isInitialLoad.current = false
        return
      }

      embedRef.current.id = embed.id
      embedRef.current.active = embed.active
      embedRef.current.profileId = profileId
      const activePos = embed.activePos
      if (activePos !== undefined && activePos > 0 && activePos < variant.history.length) {
        setNavState((state) => ({
          ...state,
          startPosition: activePos,
          position: historyNextPosition(
            treeProperties,
            lightEditingState(variant),
            activePos + 1,
            store.getState().items
          ),
          recording: false
        }))
      } else if (chunkHistory) {
        setNavState((state) => ({
          ...state,
          startPosition: historyPrevPosition(
            treeProperties,
            lightEditingState(variant),
            variant.history.length,
            store.getState().items
          ),
          position: variant.history.length,
          recording: false
        }))
      } else {
        setNavState((state) => ({
          ...state,
          position: variant.history.length,
          startPosition: undefined,
          recording: false
        }))
      }
    }
  }, [chunkHistory, embed, profileId, startFromActivePos, treeProperties, variant, store])

  const hasAscendancy = embed.type === 'passives' && embed.ascendancy != null
  React.useEffect(() => {
    setNavState((state) => ({
      ...state,
      ascendancyOpen: hasAscendancy
    }))
  }, [hasAscendancy])

  const setPosition = React.useCallback(
    (value: number | ((prev: number) => number)) =>
      setNavState((state) => ({
        ...state,
        position: typeof value === 'function' ? value(state.position) : value,
        startPosition: undefined,
        recording: false
      })),
    []
  )

  const [focus, setFocus] = React.useState<number[]>()

  const grantedNodes = useGrantedNodes()
  const makeTreeState = React.useCallback(
    (navState: NavState, variant: TreeVariant): TreeEditingState => ({
      ...navState,
      history: variant.history,
      masteries: variant.masteries,
      jewels: variant.jewels,
      tattoos: variant.tattoos,
      search,
      granted: grantedNodes,
      focus: focus ?? (editNode ? [editNode.id] : undefined)
    }),
    [search, focus, editNode, grantedNodes]
  )
  const treeState = React.useMemo(() => makeTreeState(navState, variant), [navState, variant, makeTreeState])

  const onChangeVariant = React.useMemo(
    () =>
      onChange &&
      ((value: TreeVariant | ((prev: TreeVariant) => TreeVariant)) =>
        onChange((embed) => {
          if (!('variants' in embed)) return embed
          if (!embed.variants.length) return embed
          const index = embed.variants[embed.active] ? embed.active : 0
          const prev = embed.variants[index]
          if (typeof value === 'function') value = value(prev)
          if (value === prev) return embed
          embed = { ...embed }
          embed.variants = embed.variants.slice()
          embed.variants[index] = value
          return embed
        })),
    [onChange]
  )
  const onSetJewel = React.useCallback(
    (id: number | undefined) =>
      editNode?.socket != null &&
      onChange?.((embed) => {
        if (!('variants' in embed)) return embed
        if (!embed.variants.length) return embed
        const index = embed.variants[embed.active] ? embed.active : 0
        embed = { ...embed }
        embed.variants = embed.variants.slice()
        const variant = (embed.variants[index] = { ...embed.variants[index] })
        variant.jewels = { ...variant.jewels }
        if (id != null) variant.jewels[editNode.id] = id
        else delete variant.jewels[editNode.id]
        return validateTreeEmbed(embed, items)
      }),
    [editNode, onChange, items]
  )
  const onSetTattoo = React.useCallback<LibraryClickHandler>(
    (e, id) => {
      if (!editNode?.skill) return
      onChange?.((embed) => {
        if (!('variants' in embed)) return embed
        if (!embed.variants.length) return embed
        const index = embed.variants[embed.active] ? embed.active : 0
        embed = { ...embed }
        embed.variants = embed.variants.slice()
        const variant = (embed.variants[index] = { ...embed.variants[index] })
        variant.tattoos = { ...variant.tattoos }
        if (id != null) variant.tattoos[editNode.id] = id
        else delete variant.tattoos[editNode.id]
        return embed
      })
      setEditNode(undefined)
    },
    [editNode, onChange]
  )

  const onChangeTree = React.useCallback(
    (state: TreeEditingState | ((prev: TreeEditingState) => TreeEditingState)) => {
      setNavState((navState) => {
        let value: TreeEditingState
        if (typeof state === 'function') {
          value = state(makeTreeState(navState, variant))
        } else {
          value = state
        }
        return pick(value, [
          'ascendancyOpen',
          'position',
          'startPosition',
          'hover',
          'masteryPopup',
          'recording',
          'editHistory'
        ])
      })
      onChangeVariant?.((variant) => {
        let value: TreeEditingState
        if (typeof state === 'function') {
          value = state(makeTreeState(navState, variant))
        } else {
          value = state
        }
        if (
          value.history !== variant.history ||
          value.masteries !== variant.masteries ||
          value.jewels !== variant.jewels ||
          value.tattoos !== variant.tattoos
        ) {
          return {
            ...variant,
            history: value.history,
            masteries: value.masteries,
            jewels: value.jewels,
            tattoos: value.tattoos
          }
        }
        return variant
      })
    },
    [makeTreeState, navState, variant, onChangeVariant]
  )
  const onReset = React.useCallback(async () => {
    if (!onChangeVariant) return
    const hasConfirmed = await getConfirmation({
      content: <span style={{ fontSize: '18px' }}>Are you sure you want to completely reset the tree?</span>
    })
    if (!hasConfirmed) return
    setNavState((state) => ({
      ascendancyOpen: state.ascendancyOpen,
      position: 0
    }))
    onChangeVariant((variant) => ({
      ...variant,
      history: [],
      masteries: {},
      jewels: {}
    }))
  }, [getConfirmation, onChangeVariant])

  const onPreviousStep = React.useCallback(
    () =>
      setNavState((state) => {
        const position = state.startPosition ?? state.position
        if (position <= 0) return state
        return {
          ...state,
          position: position - 1,
          startPosition: historyPrevPosition(treeProperties, lightEditingState(variant), position - 1, items),
          recording: false
        }
      }),
    [treeProperties, variant, items]
  )
  const onNextStep = React.useCallback(
    () =>
      setNavState((state) => {
        if (state.position >= variant.history.length) return state
        return {
          ...state,
          startPosition: state.position + 1,
          position: historyNextPosition(treeProperties, lightEditingState(variant), state.position + 1, items),
          recording: false
        }
      }),
    [treeProperties, variant, items]
  )

  const [onClickTree, NoteProvider, isSelecting] = useNoteEditing()

  const treeView = React.useMemo(
    () => treeViewState(treeProperties, treeState, items),
    [treeProperties, treeState, items]
  )

  const treeData = React.useMemo(() => new TreeDataProvider(treeView, items), [treeView, items])

  const [focusAction, setFocusAction] = React.useState<{ nodes: number[] } | { search: string }>()
  const onFocus = React.useCallback((action: FocusAction | undefined) => {
    if (action) {
      setFocus(action.nodes)
      if (action.center) setFocusAction({ nodes: action.nodes })
    } else {
      setFocus(undefined)
    }
  }, [])

  const treeRef = React.useRef<TreeHandle>(null)
  React.useEffect(() => {
    if (focusAction) treeRef.current?.focus(focusAction)
  }, [focusAction])

  const maxCount = React.useMemo(() => {
    if (!treeProperties.readOnly) return undefined
    return treeNodeCount(treeProperties, {
      history: variant.history,
      masteries: variant.masteries,
      jewels: variant.jewels,
      tattoos: variant.tattoos,
      position: variant.history.length
    })
  }, [variant, treeProperties])

  const showMax = maxCount ?? treeView.limit!

  const counter = (
    <div className="poe-counter">
      <span className="poe-normal">
        {treeView.count.normal}/{showMax.normal}
      </span>
      {!!treeView.count.ascendancy && (
        <span className="poe-ascendancy">
          {treeView.count.ascendancy}/{showMax.ascendancy}
        </span>
      )}
    </div>
  )

  let initialSidebarState: string | undefined
  if (!onChange) {
    if (variant.notes?.length) {
      initialSidebarState = 'poe-notes'
    } else if (embed.type === 'atlas' && embed.strategy) {
      initialSidebarState = 'poe-strategy'
    }
  }

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || (e.ctrlKey && !e.shiftKey && e.code === 'KeyZ')) {
        if (chunkHistory) {
          onPreviousStep()
        } else {
          setPosition((pos) => Math.max(0, pos - 1))
        }
      } else if (
        e.key === 'ArrowRight' ||
        (e.ctrlKey && e.code === 'KeyY') ||
        (e.ctrlKey && e.shiftKey && e.code === 'KeyZ')
      ) {
        if (chunkHistory) {
          onNextStep()
        } else {
          setPosition((pos) => Math.min(variant.history.length, pos + 1))
        }
      }
    },
    [chunkHistory, variant.history.length, onPreviousStep, onNextStep, setPosition]
  )

  const onEditNode = React.useCallback((id: number, node: NodeData) => {
    if (node.skill.is_jewel_socket) {
      setEditNode({
        id,
        socket: Data.jewel_slots[node.skill_id] ?? { charm: false, start: [] }
      })
    } else {
      setEditNode({ id, skill: node.skill })
    }
  }, [])
  const socketFilter = React.useMemo(
    () => (editNode?.socket ? makeSocketFilter(editNode.socket) : undefined),
    [editNode]
  )
  const tattooFilter = React.useMemo(() => editNode?.skill && tattooCategories(editNode.skill), [editNode])

  const combinedRef = React.useMemo(() => chainedRef(treeRef, ref), [ref])

  const initialTransform = React.useCallback(
    (container: HTMLElement) => {
      let nodeId: number
      let zoom = 1600
      if ('atlas' in treeData.state) {
        return
      } else if (treeData.state.ascendancy) {
        nodeId = treeData.tree.ascendancyRoot[treeData.state.ascendancy]
        if (treeData.state.ascendancy === 'Ascendant') zoom = 2000
      } else {
        nodeId = treeData.tree.characterRoot[treeData.state.charClass]
      }
      const node = treeData.node(nodeId)
      if (!node) return
      const scale = Math.min(container.clientWidth, container.clientHeight) / zoom
      return {
        x: container.clientWidth / 2 - node.x * scale,
        y: container.clientHeight / 2 - node.y * scale,
        scale
      }
    },
    [treeData]
  )

  return (
    <TreeDataContext.Provider value={treeData}>
      <div
        className={classNames('poe-PassiveTreeEmbed', {
          'poe-editing': !!onChange,
          'poe-preview': preview,
          'poe-compact': compact
        })}
        onKeyDown={onKeyDown}
        tabIndex={-1}>
        <TreeViewer
          tree={treeProperties}
          state={treeState}
          setState={onChangeTree}
          onClick={onClickTree}
          ref={combinedRef}
          resetTransform={resetTransform}
          onEditNode={onChange && onEditNode}
          initialTransform={compact ? initialTransform : undefined}
        />
        {editNode?.socket != null && (
          <TheJewelEditor
            id={variant.jewels[editNode!.id]}
            onSelect={onSetJewel}
            filter={socketFilter}
            onClose={() => setEditNode(undefined)}
          />
        )}
        {editNode?.skill != null && (
          <Dialog
            title="Add Tattoo"
            onClose={() => setEditNode(undefined)}
            className="poe-JewelDialog"
            resizable="nesw">
            <ItemLibrary root={tattooFilter!} onClick={onSetTattoo} />
          </Dialog>
        )}
        {!preview && !compact && (
          <div className="poe-history">
            <div
              className={classNames('poe-step poe-empty', {
                'poe-active': (navState.startPosition ?? navState.position) <= 0
              })}
              onClick={() => setPosition(0)}
            />
            {variant.history.map((id, index) => (
              <HistoryStep
                key={index}
                id={id}
                active={index + 1 >= (navState.startPosition ?? navState.position) && index + 1 <= navState.position}
                onClick={onChange ? () => setPosition(index + 1) : undefined}
                setFocus={onFocus}
              />
            ))}
            {!!(onChange && variant.history.length < 123) &&
              [...Array(123 - variant.history.length)].map((_, idx) => <div key={idx} className="poe-filler" />)}
          </div>
        )}
        {!preview && !compact && (
          <PassiveSidebarContainer initialState={initialSidebarState}>
            <PassiveTotals setFocus={onFocus} />
            {embed.type === 'atlas' && !!(onChange || embed.strategy) && (
              <AtlasStrategyBar embed={embed} onChange={onChange} />
            )}
            {(!limited || showNotes) && (
              <NoteProvider>
                <PassiveNotes embed={embed} onChange={onChange} setFocus={onFocus} />
              </NoteProvider>
            )}
          </PassiveSidebarContainer>
        )}
        {!!(!onChange && preview) && (
          <a className="poe-expand" href={preview} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon icon={faExpandArrowsAlt} />
          </a>
        )}
        {!!chunkHistory && !compact && (
          <div className="poe-history-nav">
            <div
              className={classNames('poe-button', {
                'poe-disabled': (navState.startPosition ?? navState.position) <= 0
              })}
              onClick={onPreviousStep}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </div>
            {counter}
            <div
              className={classNames('poe-button', {
                'poe-disabled': navState.position >= variant.history.length
              })}
              onClick={onNextStep}>
              <FontAwesomeIcon icon={faChevronRight} />
            </div>
          </div>
        )}
        {!!onChange && !compact && (
          <div className="poe-history-nav">
            <Tooltip tooltip="Record a respec step">
              <div
                className={classNames('poe-button poe-record', {
                  'poe-active': navState.recording
                })}
                onClick={() => onChangeTree((state) => treeToggleRecording(treeProperties, state))}>
                <FontAwesomeIcon icon={faCircle} />
              </div>
            </Tooltip>
            <Tooltip tooltip="Edit history (insert new steps in the middle instead of discarding remaining steps)">
              <div
                className={classNames('poe-button poe-edit', {
                  'poe-active': navState.editHistory,
                  'poe-disabled': navState.position >= variant.history.length
                })}
                onClick={() =>
                  navState.position < variant.history.length &&
                  setNavState((state) => ({
                    ...state,
                    editHistory: !state.editHistory
                  }))
                }>
                <FontAwesomeIcon icon={faEdit} />
              </div>
            </Tooltip>
            <Tooltip tooltip="Undo">
              <div
                className={classNames('poe-button', {
                  'poe-disabled': navState.position <= 0
                })}
                onClick={() => setPosition(Math.max(0, navState.position - 1))}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </div>
            </Tooltip>
            {counter}
            <Tooltip tooltip="Redo">
              <div
                className={classNames('poe-button', {
                  'poe-disabled': navState.position >= variant.history.length
                })}
                onClick={() => setPosition(Math.min(variant.history.length, navState.position + 1))}>
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </Tooltip>
            <Tooltip tooltip="Reset">
              <div
                className={classNames('poe-button', {
                  'poe-disabled': variant.history.length <= 0
                })}
                onClick={onReset}>
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </Tooltip>
            <ConfirmationModal />
          </div>
        )}
        {!!preview && <div className="poe-frame" />}
        {children}
      </div>
    </TreeDataContext.Provider>
  )
})

export function PassiveTreeWithSearch({ embed, ...props }: TreeEmbedProps) {
  const { preview, limited, onChange } = props
  const [search, setSearch] = React.useState('')
  const [active, setActive] = React.useState(embed.active)
  const displayEmbed = React.useMemo(() => {
    if (onChange) return embed
    return { ...embed, active }
  }, [embed, active, onChange])
  const onSetActive = React.useCallback(
    (index: number) =>
      onChange
        ? onChange((embed) => {
            if (!('variants' in embed)) return embed
            return { ...embed, active: index }
          })
        : setActive(index),
    [onChange]
  )

  React.useEffect(() => {
    if (embed.active !== undefined) {
      setActive(embed.active)
    }
  }, [embed.active])

  const treeRef = React.useRef<TreeHandle>(null)
  const onSearchKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') treeRef.current?.focus({ search })
    },
    [search]
  )
  return (
    <PassiveTreeEmbed embed={displayEmbed} {...props} search={search} ref={treeRef}>
      {(!preview || (!limited && (onChange != null || displayEmbed.variants.length > 1))) && (
        <div className="poe-tree-controls-wrap">
          <div className="poe-tree-controls">
            {!limited && <PassiveTreeVariants embed={displayEmbed} onChange={onChange} setActive={onSetActive} />}
            {!preview && (
              <div className="poe-search-new">
                <SearchInput
                  size="sm"
                  collapsible={!limited && embed.variants.length > 1}
                  value={search}
                  onChange={setSearch}
                  onKeyDown={onSearchKeyDown}
                  placeholder="Search"
                  innerClass="poe-search-new-input"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </PassiveTreeEmbed>
  )
}

export function useChangeVersion(onChange: Dispatch<SetStateAction<Embed>>) {
  const items = Store.useSelector((state) => state.items)
  return React.useCallback(
    (value: number) =>
      onChange((embed) => {
        if (!('variants' in embed)) return embed
        return validateTreeEmbed({ ...embed, version: value }, items)
      }),
    [onChange, items]
  )
}

export function SelectVersion({
  version = Data.defaultVersion,
  onChange
}: {
  version: number | undefined
  onChange: (value: number) => void
  //onChange: (value: Embed | ((prev: Embed) => Embed)) => void
}) {
  const [loading, setLoading] = React.useState(false)
  const onChangeVersion = React.useCallback(
    (opt: SelectOption<number> | undefined) => {
      if (!opt) return
      setLoading(true)
      loadTree(opt.value).then(() => {
        setLoading(false)
        onChange(opt.value)
      })
    },
    [onChange]
  )
  return (
    <Select
      size="sm"
      options={Data.versions.map((version) => ({
        value: version,
        label: `Patch ${versionNumberToString(version)}`
      }))}
      value={loading ? 0 : version}
      onChange={onChangeVersion}
      isDisabled={loading}
      className="poe-version-box"
    />
  )
}

type ClassOption = {
  value: PlayerClass
  label: string
}

export function SelectClass({ value, onChange }: { value: PlayerClass; onChange: (value: PlayerClass) => void }) {
  const character = Data.characters[value] || Data.characters[defaultPlayerClass]
  const getOptions = React.useCallback(
    (): ClassOption[] =>
      Object.entries(Data.characters).map(([id, character]) => ({
        value: id as PlayerClass,
        label: character.name
      })),
    []
  )
  const onSelect = React.useCallback((opt?: ClassOption) => opt && onChange(opt.value), [onChange])
  return <Select size="sm" className="poe-class-box" options={getOptions()} value={value} onChange={onSelect} />
}

type AscendancyOption = {
  value: string
  label: string
}

export function SelectAscendancy({
  charClass,
  value,
  onChange,
  allowEmpty,
  version
}: {
  charClass: PlayerClass
  value: string | undefined
  onChange: (value: string | undefined) => void
  allowEmpty?: boolean
  version?: number
}) {
  const character = Data.characters[charClass] || Data.characters.StrDexInt
  const ascendancy = value != null ? character.ascendancies[value] : undefined
  const getOptions = React.useCallback(
    () =>
      Object.entries(Data.characters[charClass]?.ascendancies || {}).map(([id, ascendancy]) => ({
        value: id,
        label: version === 326 ? ascendancy.league_name : ascendancy.name
      })),
    [charClass, version]
  )
  const onSelect = React.useCallback((opt?: AscendancyOption) => onChange(opt?.value), [onChange])
  return (
    <Select
      size="sm"
      className="poe-ascendancy-box"
      options={getOptions()}
      value={ascendancy ? value! : undefined}
      onChange={onSelect}
      isClearable={allowEmpty}
      placeholder="No Ascendancy"
    />
  )
}

const withTreeData = (Component: React.ComponentType<{}>) =>
  function () {
    const embed = Store.useEmbed()
    if (!('version' in embed)) return null
    return (
      <WithTreeData version={embed.version}>
        <Component />
      </WithTreeData>
    )
  }

export const PassiveEditor = withTreeData(function PassiveEditor() {
  const embed = Store.useEmbed()
  const dispatch = Store.useDispatch()
  const items = Store.useSelector((state) => state.items)
  const onChangeClass = React.useCallback(
    (value: PlayerClass) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'passives') return embed
        embed = { ...embed }
        embed.charClass = value
        embed.ascendancy = undefined
        embed.variants = EmbedStore.passives.variants
        embed.active = 0
        return embed
      }),
    [dispatch]
  )
  const onChangeAscendancy = React.useCallback(
    (value: string | undefined) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'passives') return embed
        return validateTreeEmbed({ ...embed, ascendancy: value }, items)
      }),
    [dispatch, items]
  )

  React.useEffect(() => {
    dispatch.setEmbed((embed) => {
      if (embed.type !== 'passives') return embed
      return validateTreeEmbed(embed, items)
    })
  }, [items, dispatch])

  const onChange = React.useMemo(() => dispatch.setEmbed.bind(dispatch), [dispatch])
  const onChangeVersion = useChangeVersion(onChange)

  if (embed.type !== 'passives') return null

  return (
    <div className="poe-PassiveEditor">
      <div className="poe-passive-options">
        <SelectVersion version={embed.version} onChange={onChangeVersion} />
        <SelectClass value={embed.charClass} onChange={onChangeClass} />
        <SelectAscendancy
          charClass={embed.charClass}
          value={embed.ascendancy}
          onChange={onChangeAscendancy}
          allowEmpty
          version={embed.version}
        />
        <CopyEmbedBox type="passives" />
      </div>
      <PassiveTreeWithSearch embed={embed} onChange={onChange} />
    </div>
  )
})

export const AtlasEditor = withTreeData(function AtlasEditor() {
  const embed = Store.useEmbed()
  const dispatch = Store.useDispatch()

  const onChange = React.useMemo(() => dispatch.setEmbed.bind(dispatch), [dispatch])
  const onChangeVersion = useChangeVersion(onChange)

  if (embed.type !== 'atlas') return null

  return (
    <div className="poe-PassiveEditor">
      <div className="poe-passive-options">
        <SelectVersion version={embed.version} onChange={onChangeVersion} />
        <CopyEmbedBox type="atlas" />
      </div>
      <PassiveTreeWithSearch embed={embed} onChange={onChange} />
    </div>
  )
})
